import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft, faFilter, faSyncAlt,
  faCheckCircle, faTimesCircle, faHistory,
  faExchangeAlt, faShieldAlt, faCalendarAlt, faUser,
  faChevronDown, faChevronUp
} from '@fortawesome/free-solid-svg-icons';

interface Merchant {
  id: string;
  name: string;
  company_name: string;
}

interface Transfer {
  id: string;
  reference_no: string;
  from_merchant_id: string;
  to_merchant_id: string;
  amount: number;
  description: string | null;
  status: 'completed' | 'failed' | 'reversed';
  from_balance_before: number;
  from_balance_after: number;
  to_balance_before: number;
  to_balance_after: number;
  created_by_name: string;
  created_at: string;
  from_merchant: Merchant | null;
  to_merchant: Merchant | null;
}

interface TransferLog {
  id: string;
  transfer_id: string;
  action: 'created' | 'reversed' | 'failed';
  actor_type: string;
  actor_name: string;
  ip_address: string | null;
  details: Record<string, any>;
  created_at: string;
}

const getTodayStr = () => {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
};

const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdminCompanyTransferPage: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(getTodayStr());
  const [endDate, setEndDate] = useState(getTodayStr());
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [logsMap, setLogsMap] = useState<Record<string, TransferLog[]>>({});
  const [logsLoading, setLogsLoading] = useState<string | null>(null);

  useEffect(() => {
    loadMerchants();
    loadTransfers(0);
  }, []);

  const loadMerchants = async () => {
    try {
      const res = await merchantApi.get('/admin/merchants?limit=500');
      setMerchants(res.data.data || res.data.merchants || []);
    } catch {
      // silent
    }
  };

  const loadTransfers = useCallback(async (offset = 0) => {
    try {
      setLoading(true);
      let url = `/admin/company-transfers?limit=50&offset=${offset}`;
      if (startDate)  url += `&start_date=${startDate}`;
      if (endDate)    url += `&end_date=${endDate}`;
      if (filterFrom) url += `&from_merchant=${filterFrom}`;
      if (filterTo)   url += `&to_merchant=${filterTo}`;
      const res = await merchantApi.get(url);
      setTransfers(res.data.data || []);
      setPagination(p => ({ ...p, total: res.data.pagination?.total || 0, offset }));
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, filterFrom, filterTo]);

  const toggleLogs = async (transferId: string) => {
    if (expandedId === transferId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(transferId);
    if (logsMap[transferId]) return;
    try {
      setLogsLoading(transferId);
      const res = await merchantApi.get(`/admin/company-transfers/${transferId}/logs`);
      setLogsMap(prev => ({ ...prev, [transferId]: res.data.data || [] }));
    } catch {
      toast.error('ไม่สามารถโหลด logs ได้');
    } finally {
      setLogsLoading(null);
    }
  };

  const statusBadge = (status: string) => {
    if (status === 'completed') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700"><FontAwesomeIcon icon={faCheckCircle} /> สำเร็จ</span>;
    if (status === 'reversed')  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"><FontAwesomeIcon icon={faExchangeAlt} /> ย้อนกลับ</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"><FontAwesomeIcon icon={faTimesCircle} /> ล้มเหลว</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
              <FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-white text-base" />
            </div>
            โยกเงิน Company — Logs
          </h1>
          <p className="text-gray-500 text-sm mt-1">ประวัติและ Audit Log การโอนเงินระหว่าง Merchant</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">วันที่เริ่ม</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">วันที่สิ้นสุด</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">ต้นทาง</label>
            <select value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
              <option value="">ทั้งหมด</option>
              {merchants.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">ปลายทาง</label>
            <select value={filterTo} onChange={e => setFilterTo(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
              <option value="">ทั้งหมด</option>
              {merchants.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={() => loadTransfers(0)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition">
            <FontAwesomeIcon icon={faFilter} /> ค้นหา
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faHistory} className="text-orange-500" />
            ประวัติการโอน
            <span className="ml-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{pagination.total} รายการ</span>
          </h2>
          <button onClick={() => loadTransfers(pagination.offset)}
            className="text-gray-400 hover:text-orange-500 transition text-sm">
            <FontAwesomeIcon icon={faSyncAlt} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <FontAwesomeIcon icon={faSyncAlt} className="animate-spin text-2xl mb-2" />
            <p className="text-sm">กำลังโหลด...</p>
          </div>
        ) : transfers.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FontAwesomeIcon icon={faHistory} className="text-3xl mb-2" />
            <p className="text-sm">ยังไม่มีรายการ</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {transfers.map(tx => (
              <div key={tx.id}>
                {/* Row */}
                <div className="px-5 py-4 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                          {tx.reference_no}
                        </span>
                        {statusBadge(tx.status)}
                        <span className="text-xs text-gray-400">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                          {new Date(tx.created_at).toLocaleString('th-TH')}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-gray-700 font-medium">{tx.from_merchant?.name || '-'}</span>
                        <FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-orange-400 text-xs" />
                        <span className="text-gray-700 font-medium">{tx.to_merchant?.name || '-'}</span>
                      </div>
                      {tx.description && (
                        <p className="text-xs text-gray-400 mt-1">{tx.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        <FontAwesomeIcon icon={faUser} className="mr-1" />
                        {tx.created_by_name}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-gray-900">฿{fmt(tx.amount)}</p>
                      <button
                        onClick={() => toggleLogs(tx.id)}
                        className="mt-1 text-xs text-orange-500 hover:text-orange-700 flex items-center gap-1 ml-auto"
                      >
                        <FontAwesomeIcon icon={faShieldAlt} />
                        Audit Log
                        <FontAwesomeIcon icon={expandedId === tx.id ? faChevronUp : faChevronDown} />
                      </button>
                    </div>
                  </div>

                  {/* Balance snapshot */}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-red-50 rounded-lg px-3 py-2 text-xs">
                      <p className="text-red-600 font-semibold mb-0.5 truncate">{tx.from_merchant?.name}</p>
                      <p className="text-gray-500">ก่อน: <span className="font-mono font-bold text-gray-700">฿{fmt(tx.from_balance_before)}</span></p>
                      <p className="text-gray-500">หลัง: <span className="font-mono font-bold text-red-600">฿{fmt(tx.from_balance_after)}</span></p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg px-3 py-2 text-xs">
                      <p className="text-emerald-600 font-semibold mb-0.5 truncate">{tx.to_merchant?.name}</p>
                      <p className="text-gray-500">ก่อน: <span className="font-mono font-bold text-gray-700">฿{fmt(tx.to_balance_before)}</span></p>
                      <p className="text-gray-500">หลัง: <span className="font-mono font-bold text-emerald-600">฿{fmt(tx.to_balance_after)}</span></p>
                    </div>
                  </div>
                </div>

                {/* Audit Log Expand */}
                {expandedId === tx.id && (
                  <div className="bg-slate-50 border-t border-gray-100 px-5 py-4">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-orange-400" />
                      Audit Log — {tx.reference_no}
                    </h4>
                    {logsLoading === tx.id ? (
                      <p className="text-xs text-gray-400 flex items-center gap-2">
                        <FontAwesomeIcon icon={faSyncAlt} className="animate-spin" /> กำลังโหลด logs...
                      </p>
                    ) : (logsMap[tx.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-400">ไม่มี logs</p>
                    ) : (
                      <div className="space-y-3">
                        {(logsMap[tx.id] || []).map(log => (
                          <div key={log.id} className="flex gap-3">
                            <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                              log.action === 'created'  ? 'bg-emerald-400' :
                              log.action === 'reversed' ? 'bg-amber-400' : 'bg-red-400'
                            }`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                                  log.action === 'created'  ? 'bg-emerald-100 text-emerald-700' :
                                  log.action === 'reversed' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {log.action === 'created' ? 'สร้างรายการ' : log.action === 'reversed' ? 'ย้อนกลับ' : 'ล้มเหลว'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {log.actor_name}
                                  {log.ip_address && <span className="ml-1 text-gray-400">({log.ip_address})</span>}
                                </span>
                                <span className="text-xs text-gray-400 ml-auto">
                                  {new Date(log.created_at).toLocaleString('th-TH')}
                                </span>
                              </div>
                              {log.details && (
                                <div className="mt-1.5 bg-white rounded-lg border border-gray-200 p-2.5 text-xs font-mono text-gray-600 overflow-auto max-h-48">
                                  {Object.entries(log.details).map(([k, v]) => (
                                    <div key={k} className="flex gap-2 py-0.5 border-b border-gray-50 last:border-0">
                                      <span className="text-gray-400 shrink-0 w-36">{k}:</span>
                                      <span className="text-gray-700 font-medium">
                                        {typeof v === 'number' && (k.includes('balance') || k.includes('amount'))
                                          ? `฿${fmt(v as number)}`
                                          : String(v ?? '-')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>แสดง {pagination.offset + 1}–{Math.min(pagination.offset + pagination.limit, pagination.total)} จาก {pagination.total} รายการ</span>
            <div className="flex gap-2">
              <button disabled={pagination.offset === 0}
                onClick={() => loadTransfers(Math.max(0, pagination.offset - pagination.limit))}
                className="px-3 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">ก่อนหน้า</button>
              <button disabled={pagination.offset + pagination.limit >= pagination.total}
                onClick={() => loadTransfers(pagination.offset + pagination.limit)}
                className="px-3 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">ถัดไป</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCompanyTransferPage;
